import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import makeStyles from "@mui/styles/makeStyles";
import { Typography, TextField, FormControl, InputLabel, Select, MenuItem, Chip } from "@mui/material";
import { Alert } from "@mui/material";
import { VCard } from "../../../../core/components/VCard";
import { PrimaryButton } from "../../../layout/Button/Button";
import { ValidationResult, getById, CardColour, checkNameExists } from "../../../../client/api/branchedStory";
import { list as listThemes } from "../../../../client/api/theme";

const useStyles = makeStyles((theme) => ({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3), // 24px
    },
    formCard: {
        padding: theme.spacing(3), // 24px
    },
    formField: {
        marginBottom: theme.spacing(3), // 24px
    },
    themeChips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1), // 8px
        marginTop: theme.spacing(1), // 8px
    },
    validationSummary: {
        marginBottom: theme.spacing(3), // 24px
    },
    actions: {
        display: "flex",
        gap: theme.spacing(2), // 16px
        justifyContent: "flex-end",
        marginTop: theme.spacing(3), // 24px
    },
    statsList: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: theme.spacing(2), // 16px
        marginTop: theme.spacing(2), // 16px
    },
    statItem: {
        textAlign: "center",
        padding: theme.spacing(2), // 16px
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    statValue: {
        fontSize: "1.5rem",
        fontWeight: "bold",
        color: theme.palette.primary.main,
    },
    statLabel: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        textTransform: "uppercase",
    },
}));

interface StoryMetadataFormProps {
    storyId?: number;
    validation?: ValidationResult;
    parsedStory?: import("../../../../client/api/branchedStory").ParsedStoryStructure;
    onComplete?: (data: StoryMetadata) => void;
}

interface StoryMetadata {
    storyName: string;
    author: string;
    synopsis?: string;
    themes?: number[];
    // V4 Template fields
    ageRange?: string;
    colour?: CardColour;
    resources?: string;
    resourcesROI?: string;
}

interface Theme {
    id: number;
    name: string;
    colour: string;
}

// Validation schema using Yup (legacy requirement)
const metadataSchema = Yup.object().shape({
    storyName: Yup.string().required("Story title is required").min(3, "Title must be at least 3 characters"),
    author: Yup.string().required("Author name is required").min(2, "Author name must be at least 2 characters"),
    synopsis: Yup.string().max(500, "Synopsis must be less than 500 characters"),
    themes: Yup.array().of(Yup.number()),
    // V4 Template validation
    ageRange: Yup.string(),
    colour: Yup.string(),
    resources: Yup.string().url("Please enter a valid URL for NI resources"),
    resourcesROI: Yup.string().url("Please enter a valid URL for ROI resources"),
});

export const StoryMetadataForm: React.FC<StoryMetadataFormProps> = ({
    storyId,
    validation,
    parsedStory,
    onComplete,
}) => {
    const classes = useStyles();
    const [themes, setThemes] = useState<Theme[]>([]);
    const [initialValues, setInitialValues] = useState<StoryMetadata>({
        storyName: "",
        author: "",
        synopsis: "",
        themes: [],
        // V4 Template defaults
        ageRange: "",
        colour: "Green",
        resources: "",
        resourcesROI: "",
    });

    // Load themes and populate form with parsed story from DOCX upload
    useEffect(() => {
        const loadData = async () => {
            console.log(`=== StoryMetadataForm: Loading data ===`);
            console.log(`storyId: ${storyId}`);
            console.log(`parsedStory:`, parsedStory);

            try {
                // Load themes
                const themesResponse = await listThemes({});
                console.log(`Loaded ${themesResponse.items?.length || 0} themes`);
                setThemes(themesResponse.items || []);

                // Use parsed story from upload (Step 1) to populate the form
                if (parsedStory) {
                    console.log(`Using parsed story from upload`);

                    // Match parsed theme names to theme IDs from database
                    let matchedThemeIds: number[] = [];
                    if (parsedStory.themes && themesResponse.items) {
                        const parsedThemeNames = parsedStory.themes
                            .split(",")
                            .map((t: string) => t.trim().toLowerCase())
                            .filter(Boolean);
                        matchedThemeIds = themesResponse.items
                            .filter((t) => parsedThemeNames.includes(t.name.toLowerCase()))
                            .map((t) => t.id);
                        console.log(`Matched ${matchedThemeIds.length} of ${parsedThemeNames.length} themes from DOCX`);
                    }

                    // Match parsed age range to available dropdown values
                    // DOCX may have "11-14" but dropdown has "11-12", "12-13", etc.
                    // Find the first range that starts with the same lower bound
                    let matchedAgeRange = parsedStory.ageRange || "";
                    if (matchedAgeRange) {
                        const ageRanges: string[] = [];
                        for (let i = 4; i <= 17; i++) {
                            ageRanges.push(`${i}-${i + 1}`);
                        }
                        ageRanges.push("18+");
                        if (!ageRanges.includes(matchedAgeRange)) {
                            // Try to find a matching range by lower bound
                            const lowerBound = matchedAgeRange.split("-")[0]?.trim();
                            const found = ageRanges.find((r) => r.startsWith(lowerBound + "-"));
                            if (found) {
                                matchedAgeRange = found;
                                console.log(`Age range "${parsedStory.ageRange}" mapped to "${matchedAgeRange}"`);
                            }
                        }
                    }

                    const newInitialValues = {
                        storyName: parsedStory.title || "",
                        author: parsedStory.author || "",
                        synopsis: parsedStory.synopsis || "",
                        themes: matchedThemeIds,
                        ageRange: matchedAgeRange,
                        colour: (parsedStory.colour as CardColour) || "Green",
                        resources: parsedStory.resources || "",
                        resourcesROI: parsedStory.resourcesROI || "",
                    };

                    console.log(`Setting initialValues from parsed story:`, newInitialValues);
                    setInitialValues(newInitialValues);
                } else if (storyId) {
                    // Fallback: Load from database if editing existing story
                    console.log(`Fetching story data from database for ID: ${storyId}`);
                    const story = await getById(storyId);
                    console.log(`Story data fetched:`, story);

                    const newInitialValues = {
                        storyName: story.storyName || "",
                        author: story.author || "",
                        synopsis: story.synopsis || "",
                        themes: story.Themes?.map((t) => t.id) || [],
                        ageRange: story.ageRange || "",
                        colour: story.colour || "Green",
                        resources: story.resources || "",
                        resourcesROI: story.resourcesROI || "",
                    };

                    console.log(`Setting initialValues from database:`, newInitialValues);
                    setInitialValues(newInitialValues);
                } else {
                    console.log(`No storyId or parsedStory, using empty initial values`);
                }
            } catch (error) {
                console.error("Error loading data:", error);
            }
        };

        loadData();
    }, [storyId, parsedStory]);

    const handleSubmit = async (
        values: StoryMetadata,
        { setFieldError }: { setFieldError: (field: string, message: string) => void },
    ) => {
        // Check if story name already exists before proceeding
        if (values.storyName) {
            try {
                const nameCheck = await checkNameExists(values.storyName);
                if (nameCheck.exists) {
                    setFieldError(
                        "storyName",
                        `A story named "${values.storyName}" already exists. Please choose a different name.`,
                    );
                    return;
                }
            } catch {
                // If check fails, let it through — the save endpoint will catch duplicates
            }
        }

        if (onComplete) {
            onComplete(values);
        }
    };

    const renderValidationSummary = () => {
        if (!validation) return null;

        const { valid, stats } = validation;

        return (
            <VCard className={classes.validationSummary}>
                <Typography variant="h6" gutterBottom>
                    Story Analysis
                </Typography>

                {valid ? (
                    <Alert severity="success" style={{ marginBottom: 16 }}>
                        Your story structure is valid and ready to use!
                    </Alert>
                ) : (
                    <Alert severity="warning" style={{ marginBottom: 16 }}>
                        Story uploaded with warnings. Please review the structure before publishing.
                    </Alert>
                )}

                <div className={classes.statsList}>
                    <div className={classes.statItem}>
                        <div className={classes.statValue}>{stats.totalParts}</div>
                        <div className={classes.statLabel}>Story Parts</div>
                    </div>
                    <div className={classes.statItem}>
                        <div className={classes.statValue}>{stats.totalDecisionPoints}</div>
                        <div className={classes.statLabel}>Decision Points</div>
                    </div>
                    <div className={classes.statItem}>
                        <div className={classes.statValue}>{stats.totalBranches}</div>
                        <div className={classes.statLabel}>Total Branches</div>
                    </div>
                    <div className={classes.statItem}>
                        <div className={classes.statValue}>{stats.maxDepth}</div>
                        <div className={classes.statLabel}>Max Depth</div>
                    </div>
                    <div className={classes.statItem}>
                        <div className={classes.statValue}>{stats.endPoints}</div>
                        <div className={classes.statLabel}>End Points</div>
                    </div>
                </div>
            </VCard>
        );
    };

    return (
        <div className={classes.container}>
            {renderValidationSummary()}
            <VCard className={classes.formCard}>
                <Typography variant="h6" gutterBottom>
                    Story Information
                </Typography>

                <Formik
                    initialValues={initialValues}
                    validationSchema={metadataSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                        <Form>
                            <TextField
                                variant="outlined"
                                fullWidth
                                id="storyName"
                                name="storyName"
                                label="Story Title"
                                value={values.storyName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.storyName && Boolean(errors.storyName)}
                                helperText={touched.storyName && errors.storyName}
                                className={classes.formField}
                                required
                            />

                            <TextField
                                variant="outlined"
                                fullWidth
                                id="author"
                                name="author"
                                label="Author"
                                value={values.author}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.author && Boolean(errors.author)}
                                helperText={touched.author && errors.author}
                                className={classes.formField}
                                required
                            />

                            <TextField
                                variant="outlined"
                                fullWidth
                                id="synopsis"
                                name="synopsis"
                                label="Synopsis"
                                multiline
                                rows={3}
                                value={values.synopsis}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.synopsis && Boolean(errors.synopsis)}
                                helperText={
                                    (touched.synopsis && errors.synopsis) ||
                                    "Brief description of your branched story (optional)"
                                }
                                className={classes.formField}
                            />

                            <FormControl variant="outlined" fullWidth className={classes.formField}>
                                <InputLabel id="themes-label">Themes</InputLabel>
                                <Select
                                    variant="outlined"
                                    labelId="themes-label"
                                    id="themes"
                                    label="Themes"
                                    multiple
                                    value={values.themes || []}
                                    onChange={(event) => setFieldValue("themes", event.target.value)}
                                    renderValue={(selected) => (
                                        <div className={classes.themeChips}>
                                            {(selected as number[]).map((themeId) => {
                                                const theme = themes.find((t) => t.id === themeId);
                                                return (
                                                    <Chip
                                                        key={themeId}
                                                        label={theme?.name || `Theme ${themeId}`}
                                                        size="small"
                                                    />
                                                );
                                            })}
                                        </div>
                                    )}
                                >
                                    {themes.map((theme) => (
                                        <MenuItem key={theme.id} value={theme.id}>
                                            {theme.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* V4 Template Fields */}
                            <FormControl variant="outlined" fullWidth className={classes.formField}>
                                <InputLabel id="age-range-label">Age Range</InputLabel>
                                <Select
                                    variant="outlined"
                                    labelId="age-range-label"
                                    id="ageRange"
                                    label="Age Range"
                                    value={values.ageRange || ""}
                                    onChange={(event) => setFieldValue("ageRange", event.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Select age range (optional)</em>
                                    </MenuItem>
                                    {(() => {
                                        const ageRanges: string[] = [];
                                        for (let i = 4; i < 17; i++) {
                                            ageRanges.push(`${i}-${i + 1}`);
                                        }
                                        ageRanges.push("18+");
                                        return ageRanges.map((range) => (
                                            <MenuItem key={range} value={range}>
                                                {range}
                                            </MenuItem>
                                        ));
                                    })()}
                                </Select>
                            </FormControl>

                            <FormControl variant="outlined" fullWidth className={classes.formField}>
                                <InputLabel id="colour-label">Card Color</InputLabel>
                                <Select
                                    variant="outlined"
                                    labelId="colour-label"
                                    id="colour"
                                    label="Card Color"
                                    value={values.colour || "Green"}
                                    onChange={(event) => setFieldValue("colour", event.target.value)}
                                >
                                    {[
                                        "Red",
                                        "Orange",
                                        "Yellow",
                                        "Green",
                                        "Cyan",
                                        "Blue",
                                        "Magneta",
                                        "Crimson",
                                        "Silver",
                                        "Bronze",
                                        "BrightYellow",
                                        "Aqua",
                                        "Purple",
                                        "BrightOrange",
                                        "BrightRed",
                                        "LightGreen",
                                        "NightBlue",
                                    ].map((colour) => (
                                        <MenuItem key={colour} value={colour}>
                                            {colour}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                variant="outlined"
                                fullWidth
                                id="resources"
                                name="resources"
                                label="NI Resources Link"
                                value={values.resources}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.resources && Boolean(errors.resources)}
                                helperText={
                                    (touched.resources && errors.resources) ||
                                    "Link to Northern Ireland specific resources (optional)"
                                }
                                className={classes.formField}
                                placeholder="https://..."
                            />

                            <TextField
                                variant="outlined"
                                fullWidth
                                id="resourcesROI"
                                name="resourcesROI"
                                label="ROI Resources Link"
                                value={values.resourcesROI}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.resourcesROI && Boolean(errors.resourcesROI)}
                                helperText={
                                    (touched.resourcesROI && errors.resourcesROI) ||
                                    "Link to Republic of Ireland specific resources (optional)"
                                }
                                className={classes.formField}
                                placeholder="https://..."
                            />

                            <div className={classes.actions}>
                                <PrimaryButton type="submit">Continue</PrimaryButton>
                            </div>
                        </Form>
                    )}
                </Formik>
            </VCard>
        </div>
    );
};
